import { useEffect, useRef } from "react";

/**
 * Runs `fn` on an interval while the tab is visible.
 *
 * This is how a child's dashboard notices money arriving without a refresh.
 * Polling rather than a websocket is deliberate: the server is a single small
 * Go binary behind Cloudflare, allowance money lands at most a few times a day,
 * and a poll costs one cheap request against an already-derived balance. A
 * socket would add reconnect handling and a stateful server for no benefit a
 * child could perceive.
 *
 * Polling pauses while the tab is hidden — a backgrounded phone should not keep
 * waking the server — and fires once immediately on return, so a child who
 * switches back sees the current number straight away.
 */
export function usePoll(fn: () => void | Promise<void>, intervalMs: number) {
  // Held in a ref so a caller does not have to memoise `fn` to avoid
  // restarting the timer on every render.
  const saved = useRef(fn);
  useEffect(() => {
    saved.current = fn;
  }, [fn]);

  useEffect(() => {
    let timer: number | undefined;

    const run = () => void saved.current();

    const start = () => {
      stop();
      timer = window.setInterval(run, intervalMs);
    };

    const stop = () => {
      if (timer !== undefined) {
        window.clearInterval(timer);
        timer = undefined;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        run();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
